import AddIcon from '@mui/icons-material/Add';
import { Box, Chip, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Editor } from '@tiptap/react';
import React from 'react';
import type { Section, Tender } from '../../types/tender';
import SectionItem from './ReviewSections/SectionItem';
import { SortableSectionItem } from './ReviewSections/SortableSectionItem';

export interface ReviewSectionRowProps {
  section: Section;
  fullIndex: number;
  nextSectionIndex?: number;
  activeId: string | null;
  expandedSectionIds: Set<string>;
  // biome-ignore lint/suspicious/noExplicitAny: complex type
  comparisonData: any;
  tenderId: string;
  tender: Tender;
  account: import('../../types/auth').User | null;
  editingSectionId: string | null;
  editContent: string;
  setEditContent: (content: string) => void;

  activeEditor: Editor | null;

  setActiveEditor: (editor: Editor | null) => void;
  showDiffs: Record<string, boolean>;
  setShowDiffs: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  inlineResolved: Record<string, boolean>;
  chatSectionId: string | null;
  setChatSectionId: (id: string | null) => void;
  downloadingSectionId: string | null;
  // biome-ignore lint/suspicious/noExplicitAny: complex type
  sectionHandlers: any;
  historyComparisonSectionId: string | null;
  canEditAllSections: boolean;
  setExpandedSectionIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleOpenAddDialog: (index: number) => void;
  isFirstForm: boolean;
  showSeparator: boolean;
  isMainHeader: boolean;
  isFormHeader: boolean;
  currentFileId: string | null;
  formMetrics: {
    totalTags: number;
    filledTags: number;
    emptyTags: number;
  } | null;
}

export const ReviewSectionRow: React.FC<ReviewSectionRowProps> = ({
  section,
  fullIndex,
  nextSectionIndex,
  activeId,
  expandedSectionIds,
  comparisonData,
  tenderId,
  tender,
  account,
  editingSectionId,
  editContent,
  setEditContent,
  activeEditor,
  setActiveEditor,
  showDiffs,
  setShowDiffs,
  inlineResolved,
  chatSectionId,
  setChatSectionId,
  downloadingSectionId,
  sectionHandlers,
  historyComparisonSectionId,
  canEditAllSections,
  setExpandedSectionIds,
  handleOpenAddDialog,
  isFirstForm,
  showSeparator,
  isMainHeader,
  isFormHeader,
  currentFileId,
  formMetrics,
}) => {
  const theme = useTheme();

  const expanded = expandedSectionIds.has(section.id);

  let displaySection = section;
  if (comparisonData && comparisonData.sectionId === section.id) {
    displaySection = {
      ...section,
      content: comparisonData.content,
      suggested_content: comparisonData.suggested_content,
    };
  }

  const currentIndex = section.index;
  const showTopDivider = fullIndex === 0;
  const topInsertIndex = currentIndex / 2;
  const nextIdx = nextSectionIndex !== undefined ? nextSectionIndex : currentIndex + 1;
  const bottomInsertIndex = (currentIndex + nextIdx) / 2;

  return (
    <React.Fragment key={`frag-${section.id}`}>
      {!activeId && isFirstForm && (
        <Box
          sx={{
            mt: 4,
            mb: 2,
            p: 2,
            backgroundColor: (theme) => `${theme.palette.info.main}1A`,
            borderRadius: 2,
            border: 1,
            borderColor: 'info.main',
          }}
        >
          <Typography variant="h6" sx={{ color: 'info.main', fontWeight: 'bold' }}>
            Form Sections
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1 }}>
            Only sections with more than 20 words are shown below. Short responses are not able to
            be edited here.
          </Typography>
        </Box>
      )}
      {!activeId && showSeparator && <Divider sx={{ my: 4, borderColor: theme.palette.divider }} />}{' '}
      {!activeId && isMainHeader && (
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            mb: 2,
            ml: 1,
            fontWeight: 'bold',
            color: 'text.secondary',
          }}
        >
          Proposal Structure
        </Typography>
      )}
      {!activeId && isFormHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mt: 2,
            mb: 2,
            ml: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: 'text.secondary',
            }}
          >
            Form: {currentFileId}
          </Typography>
          {formMetrics &&
            (formMetrics.totalTags > 0 ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  size="medium"
                  label={`Total Tags: ${formMetrics.totalTags}`}
                  variant="filled"
                  color="info"
                />
                <Chip
                  size="medium"
                  label={`Filled: ${formMetrics.filledTags}`}
                  color="success"
                  variant="filled"
                />
                <Chip
                  size="medium"
                  label={`Empty/Placeholder: ${formMetrics.emptyTags}`}
                  color="warning"
                  variant="filled"
                  sx={{ color: 'common.white' }}
                />
              </Box>
            ) : (
              <Chip size="medium" label="No Tags Found" variant="filled" color="default" />
            ))}
        </Box>
      )}
      <SortableSectionItem id={section.id}>
        {showTopDivider && !activeId && (
          <Box
            onClick={() => handleOpenAddDialog(topInsertIndex)}
            className="add-section-trigger"
            sx={{
              height: '16px',
              width: '100%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              my: 0,
              position: 'relative',
              zIndex: 1,
              transition: 'all 0.3s ease',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-20px',
                left: 0,
                right: 0,
                height: '20px',
                background: 'transparent',
              },
              '&:hover': {
                height: '50px',
                opacity: 1,
                zIndex: 100,
                transitionDelay: '0.2s',
              },
            }}
            title="Add section here"
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                pr: 2,
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 3,
                  mr: 2,
                  transition: 'transform 0.2s',
                  '.add-section-trigger:hover &': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </Box>
              <Divider sx={{ flexGrow: 1, borderColor: theme.palette.primary.main }}>
                <Chip
                  label="Add Section"
                  size="small"
                  sx={{
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    bgcolor: theme.palette.bidlogiq.brick,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.palette.bidlogiq.rust,
                    },
                  }}
                />
              </Divider>
            </Box>
          </Box>
        )}
        <SectionItem
          section={displaySection}
          tenderId={tenderId}
          account={account}
          expanded={expanded}
          onChange={(_e, isExpanded) => {
            setExpandedSectionIds((prev) => {
              const next = new Set(prev);
              if (isExpanded) {
                next.add(section.id);
              } else {
                next.delete(section.id);
              }
              return next;
            });
            if (!isExpanded && editingSectionId === section.id) {
              sectionHandlers.handleSave(section);
            }
          }}
          editingSectionId={editingSectionId}
          editContent={editContent}
          setEditContent={setEditContent}
          activeEditor={activeEditor}
          setActiveEditor={setActiveEditor}
          showDiffs={showDiffs}
          setShowDiffs={setShowDiffs}
          inlineResolved={inlineResolved}
          chatSectionId={chatSectionId}
          setChatSectionId={setChatSectionId}
          downloadingSectionId={downloadingSectionId}
          handlers={sectionHandlers}
          isHistoryComparison={historyComparisonSectionId === section.id}
          isOwner={tender.ownerId === account?.uid}
          canEditAllSections={canEditAllSections}
        />
        <Box
          onClick={() => handleOpenAddDialog(bottomInsertIndex)}
          className="add-section-trigger"
          sx={{
            height: '16px',
            width: '100%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            my: 0,
            position: 'relative',
            zIndex: 1,
            transition: 'all 0.3s ease',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-10px',
              left: 0,
              right: 0,
              height: '10px',
              background: 'transparent',
            },
            '&:hover': {
              height: '50px',
              opacity: 1,
              zIndex: 100,
              transitionDelay: '0.2s',
            },
          }}
          title="Add section here"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: 'success.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 3,
                mr: 2,
                transition: 'transform 0.2s',
                '.add-section-trigger:hover &': {
                  transform: 'scale(1.1)',
                },
              }}
            >
              <AddIcon sx={{ fontSize: 20 }} />
            </Box>
            <Divider sx={{ flexGrow: 1, borderColor: theme.palette.primary.main }}>
              <Chip
                label="Add Section"
                size="small"
                sx={{
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  bgcolor: theme.palette.bidlogiq.brick,
                  color: 'white',
                  '&:hover': {
                    bgcolor: theme.palette.bidlogiq.rust,
                  },
                }}
              />
            </Divider>
          </Box>
        </Box>
      </SortableSectionItem>
    </React.Fragment>
  );
};
